import { queryKeys } from './queryKeys';
import { warmEventMap } from '../festivalOffline';
import { requestFindPeersRefresh } from '../proximity/data/findPeersRefresh';

/** Invalidate migrated query caches after mutations from non-migrated screens. */

export function invalidateAfterFollow(queryClient, { viewerId, targetUserId } = {}) {
  if (!queryClient) return;
  queryClient.invalidateQueries({ queryKey: ['profiles'] });
  if (viewerId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.inbox(viewerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.following(viewerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.followers(viewerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.suggested(viewerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.suggestedDiscover(viewerId) });
  }
  if (targetUserId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.profiles.detail(targetUserId, 'public') });
    queryClient.invalidateQueries({ queryKey: queryKeys.profiles.detail(targetUserId, 'own') });
    queryClient.invalidateQueries({ queryKey: queryKeys.profiles.events(targetUserId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.followers(targetUserId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.people.following(targetUserId) });
  }
  // A follow-back makes a mutual follow, and mutual follows going to the event are on Find's roster: re-read it now,
  // not on the 45 s timer (2026-10-16 audit). An unfollow drops someone the same way.
  requestFindPeersRefresh('follow');
}

export function invalidateAfterRsvpChange(queryClient, { userId, eventId } = {}) {
  if (!queryClient) return;
  if (userId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.rsvps.mine(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.pending(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.inbox(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.profiles.events(userId) });
  }
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.events.detailRsvps(eventId) });
  }
  queryClient.invalidateQueries({ queryKey: ['events', 'feed'] });
  queryClient.invalidateQueries({ queryKey: ['events', 'trending'] });
  queryClient.invalidateQueries({ queryKey: ['events', 'search'] });
  // Join -> "Create a group" goes through here, not the membership path: without this the new group was missing from
  // Find, the Groups tab and the map's people for up to 10 minutes (pre-Head Trip audit, 2026-10-07).
  queryClient.invalidateQueries({ queryKey: ['find'] });
  queryClient.invalidateQueries({ queryKey: ['groups'] });
  // Maps lists only the events you are going to (2026-10-12), from list_my_festival_maps - cached 20 min. Sam went solo to
  // Miley and the Maps tab said he was going to nothing.
  if (userId) queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.mine(userId) });
}

export function invalidateAfterGroupMembershipChange(
  queryClient,
  { userId, groupId, eventId } = {},
) {
  if (!queryClient) return;
  if (userId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.inbox(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.pending(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.rsvps.mine(userId) });
  }
  if (groupId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.group(groupId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.groupMessages(groupId) });
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chats.groupMembership(groupId, userId),
      });
    }
  }
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.events.detailRsvps(eventId) });
  }
  // The Groups tab, Find and the Maps people layer all read one cached list (['find','groups',uid]). A membership
  // change that did not refresh it left a person "not in a group" on three screens at once (2026-10-03).
  queryClient.invalidateQueries({ queryKey: ['find'] });
  queryClient.invalidateQueries({ queryKey: ['groups'] });
  if (userId) queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.mine(userId) }); // Maps lists your groups' events too
  // A running Find session re-reads who to watch now, not on its 45 s timer (2026-10-14 Turnstile).
  requestFindPeersRefresh('membership_change');
}

export function invalidateAfterBlockChange(queryClient, { viewerId, targetUserId } = {}) {
  if (!queryClient) return;
  invalidateAfterFollow(queryClient, { viewerId, targetUserId });
  if (viewerId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.inbox(viewerId) });
  }
}

export function invalidateAfterNotificationRead(queryClient, { userId } = {}) {
  if (!queryClient || !userId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
  queryClient.invalidateQueries({ queryKey: ['notifications', 'list', userId] });
}

/**
 * After a match_user_to_group / confirm_rejoin_group call made outside useRsvpWrite (rejoin, skip, find a group): the
 * RPC wrote the RSVP or the membership, so refresh what the membership path refreshes - Maps' list among it - and
 * start the event's map. These paths used to refresh nothing; Maps waited out its 20-minute cache (2026-10-13 audit).
 */
export function afterMatchRpc(queryClient, { userId, eventId, groupId } = {}) {
  if (!queryClient) return;
  invalidateAfterGroupMembershipChange(queryClient, { userId, groupId, eventId });
  warmEventMap(queryClient, eventId);
}
